import { BaseSecurityModule } from '../base.security.module';
import { RightRuleAction, SecurityModuleDomain, SecurityModuleName, SecurityRight, } from '../security.module';
import { TenantSubscription } from '../../entities/tenant/tenant';

export enum MobileTechniciansSecurityRight {
    CanAccessJobs = 50000,
    CanAccessQuotes = 50001,
    CanAccessPurchaseOrders = 50002,
    CanAccessCustomers = 50003,
    ManageJobs = 50004,
    ManageQuotes = 50005,
    ManagePurchaseOrders = 50006,
    ManageCustomers = 50007,
    ManageCodesOnJob = 50008,
    CanAccessRunsheets = 50009,
    CanAccessAppointments = 50010,
    CanCompleteJobs = 50011,
    ManageJobManageAssets = 50012,
    CanAccessTimesheets = 50013,
    ManageMyTimesheets = 50014,
    CanTakePayments = 50015,
    ModifyFormAfterSubmission = 50016,
    AllowEditBeforeCheckIn = 50017,
    AllowSmsActivities = 50018,
    AllowEditableMessages = 50019,
    CanAccessSupervisor = 50020,
    ManageJobViewTasks = 50021,
    ManageJobViewFiles = 50022,
    ManageJobViewItems = 50023,
    ManageJobViewAssets = 50024,
    ManageJobViewDefects = 50025,
    ManageJobViewInvoices = 50026,
    ManageResources = 50027,
    ManagePendingItems = 50028,
    ManageAppointments = 50029,
    ManageMyActivitiesWhileJobIsOpen = 50030,
    ManageAllActivities = 50031,
    CanAccessWorkforceCompliance = 50032,
    ManageJobManageMyTimesheets = 50033,
    ManageJobManageOtherUserTimesheets = 50034,
    CanCheckoutJobs = 50035,
    CanPutJobsOnHold = 50036,
    ManageJobViewEquipments = 50037,
    ManageJobManageEquipments = 50038,
    CanAccessStockLocations = 50039,
    CanAccessStandaloneForms = 50040,
    AccessAllFieldActivities = 50041,
    ManageOfficeCodesOnJob = 50042,
    SearchAllAssets = 50043,
    // Sections / Tabs rights (WBS-11925): each row's View is the existing "Manage Job: View …" right; the Assets / Equipment
    // writes replace Manage Assets / Manage Equipment, which stay as deprecated while the Field App still reads them.
    TasksEdit = 50044,
    ItemsCreate = 50045,
    ItemsEdit = 50046,
    ItemsDelete = 50047,
    SorsView = 50048,
    SorsCreate = 50049,
    SorsEdit = 50050,
    SorsDelete = 50051,
    AssetsCreate = 50052,
    AssetsEdit = 50053,
    AssetsDelete = 50054,
    InvoicesCreate = 50055,
    InvoicesEdit = 50056,
    EquipmentCreate = 50057,
    EquipmentEdit = 50058,
    EquipmentDelete = 50059,
    // WBS-12796: off for every role by default; honoured by the Field App only, no server check.
    FieldAppBulkActions = 50060,
}

export class MobileTechniciansSecurityModule extends BaseSecurityModule<MobileTechniciansSecurityRight> {

    constructor() {

        super(
            SecurityModuleName.MobileTechnicians,
            [
                { _id: MobileTechniciansSecurityRight.CanAccessJobs, name: 'Access Work - Jobs', basic: true },
                { _id: MobileTechniciansSecurityRight.CanAccessAppointments, name: 'Access Work - Appointments', basic: true },
                { _id: MobileTechniciansSecurityRight.CanAccessRunsheets, name: 'Access Work - Runsheet', basic: true },
                { _id: MobileTechniciansSecurityRight.CanAccessQuotes, name: 'Access Quotes (Legacy)', basic: true },
                { _id: MobileTechniciansSecurityRight.CanAccessPurchaseOrders, name: 'Access Purchase Orders', basic: true, subscription: TenantSubscription.Professional },
                { _id: MobileTechniciansSecurityRight.ManageJobs, name: 'Manage Job: Create New', basic: false },
                { _id: MobileTechniciansSecurityRight.ManageCodesOnJob, name: 'Manage Job: Manage Field Items & SORs', basic: false },
                { _id: MobileTechniciansSecurityRight.ManageOfficeCodesOnJob, name: 'Manage Job: Manage Office Items & SORs', basic: false },
                { _id: MobileTechniciansSecurityRight.ManagePendingItems, name: 'Manage Job: Manage Pending Items & SORs', basic: false },
                { _id: MobileTechniciansSecurityRight.ManageQuotes, name: 'Manage Quotes (Legacy)', basic: false },
                { _id: MobileTechniciansSecurityRight.ManagePurchaseOrders, name: 'Manage Purchase Orders', basic: false, subscription: TenantSubscription.Professional },
                { _id: MobileTechniciansSecurityRight.CanCompleteJobs, name: 'Allow Complete Jobs', basic: false },
                { _id: MobileTechniciansSecurityRight.ManageJobManageAssets, name: 'Manage Job: Manage Assets', basic: false, module: SecurityModuleName.Assets },
                { _id: MobileTechniciansSecurityRight.CanAccessTimesheets, name: 'Access Timesheets', basic: true, },
                { _id: MobileTechniciansSecurityRight.ManageMyTimesheets, name: 'Manage My Timesheets', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanTakePayments, name: 'Take Payments', basic: false, },
                { _id: MobileTechniciansSecurityRight.ModifyFormAfterSubmission, name: 'Modify Form After Submission', basic: false, },
                { _id: MobileTechniciansSecurityRight.AllowEditBeforeCheckIn, name: 'Allow Edit Before Check In', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanCheckoutJobs, name: 'Allow Checkout Jobs', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanPutJobsOnHold, name: 'Allow Put Jobs On Hold', basic: false, },
                { _id: MobileTechniciansSecurityRight.AllowSmsActivities, name: 'SMS - Allow Text Messaging', basic: false, },
                { _id: MobileTechniciansSecurityRight.AllowEditableMessages, name: 'SMS - Allow editable messages', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanAccessSupervisor, name: 'Access Supervisor', basic: true, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewTasks, name: 'Manage Job: View Tasks', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewFiles, name: 'Manage Job: View Files', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewItems, name: 'Manage Job: View Items', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewAssets, name: 'Manage Job: View Assets', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewDefects, name: 'Manage Job: View Defects', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewInvoices, name: 'Manage Job: View Invoices', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobManageMyTimesheets, name: 'Manage Job: Manage My Timesheets', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobManageOtherUserTimesheets, name: "Manage Job: Manage Other User's Timesheets", basic: false },
                { _id: MobileTechniciansSecurityRight.ManageResources, name: 'Manage Resources', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageAppointments, name: 'Manage Appointments', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageMyActivitiesWhileJobIsOpen, name: 'Manage My Activities (While Job Is Open)', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageAllActivities, name: 'Manage All Activities', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanAccessWorkforceCompliance, name: 'Access Workforce Compliance', basic: true, },
                { _id: MobileTechniciansSecurityRight.ManageJobViewEquipments, name: 'Manage Job: View Equipment', basic: false, },
                { _id: MobileTechniciansSecurityRight.ManageJobManageEquipments, name: 'Manage Job: Manage Equipment', basic: false, },
                { _id: MobileTechniciansSecurityRight.CanAccessStockLocations, name: 'Access My Assets', basic: true, },
                { _id: MobileTechniciansSecurityRight.CanAccessStandaloneForms, name: 'Access Standalone Forms', basic: true, },
                { _id: MobileTechniciansSecurityRight.AccessAllFieldActivities, name: 'Access All Field Activities', basic: false, },
                { _id: MobileTechniciansSecurityRight.SearchAllAssets, name: 'Search All Assets (In House & Hireable)', basic: false, module: SecurityModuleName.Equipment },
                { _id: MobileTechniciansSecurityRight.TasksEdit, name: 'Tasks › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.ItemsCreate, name: 'Items › Create', basic: false },
                { _id: MobileTechniciansSecurityRight.ItemsEdit, name: 'Items › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.ItemsDelete, name: 'Items › Delete', basic: false },
                { _id: MobileTechniciansSecurityRight.SorsView, name: 'SORs › View', basic: false },
                { _id: MobileTechniciansSecurityRight.SorsCreate, name: 'SORs › Create', basic: false },
                { _id: MobileTechniciansSecurityRight.SorsEdit, name: 'SORs › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.SorsDelete, name: 'SORs › Delete', basic: false },
                { _id: MobileTechniciansSecurityRight.AssetsCreate, name: 'Assets › Create', basic: false },
                { _id: MobileTechniciansSecurityRight.AssetsEdit, name: 'Assets › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.AssetsDelete, name: 'Assets › Delete', basic: false },
                { _id: MobileTechniciansSecurityRight.InvoicesCreate, name: 'Invoices › Create', basic: false },
                { _id: MobileTechniciansSecurityRight.InvoicesEdit, name: 'Invoices › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.EquipmentCreate, name: 'Equipment › Create', basic: false },
                { _id: MobileTechniciansSecurityRight.EquipmentEdit, name: 'Equipment › Edit', basic: false },
                { _id: MobileTechniciansSecurityRight.EquipmentDelete, name: 'Equipment › Delete', basic: false },
                { _id: MobileTechniciansSecurityRight.FieldAppBulkActions, name: 'Allow bulk actions on Tasks, SORs & Items', basic: false },
            ] as SecurityRight<MobileTechniciansSecurityRight>[],
            SecurityModuleDomain.Mobile
        );

        this.rules = [
            {
                _id: MobileTechniciansSecurityRight.ManageJobManageMyTimesheets,
                value: false,
                action: RightRuleAction.Disable,
                target: [MobileTechniciansSecurityRight.ManageJobManageOtherUserTimesheets],
            },
            {
                _id: MobileTechniciansSecurityRight.ManageJobManageMyTimesheets,
                value: true,
                action: RightRuleAction.Enable,
                target: [MobileTechniciansSecurityRight.ManageJobManageOtherUserTimesheets],
            }
        ];
    }

}
